/**
 * Admin-Tab „Identity" (G-28): Sync-Journal + „Voll-Sync"-Button.
 * Bei Dry-Run (keine Logto-M2M-Credentials konfiguriert) erscheint ein
 * Hinweis-Banner — der Status-Response liefert dryRun direkt mit.
 */

import { useMemo } from "react"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { AlertTriangle, RefreshCw } from "lucide-react"
import { toast } from "sonner"
import { LoadingSpinner } from "@/components/shared"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import { DataGrid, type ColumnDef } from "@/components/ui/data-grid"
import { api, apiError } from "@/services/api"
import type { IdentitySyncJournalEntry } from "@/types"

function fmtTimestamp(iso: string): string {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return iso
  return new Intl.DateTimeFormat("de-DE", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(d)
}

const EVENT_BADGE: Record<string, string> = {
  error: "border-red-300 bg-red-100 text-red-800",
}

export function IdentityTab() {
  const queryClient = useQueryClient()

  const status = useQuery({
    queryKey: ["identity", "status"],
    queryFn: () => api.identity.status(),
  })

  const sync = useMutation({
    mutationFn: () => api.identity.sync(),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ["identity"] })
      const suffix = result.dryRun ? " (Dry-Run — es wurde nichts geschrieben)" : ""
      toast.success(
        `Voll-Sync abgeschlossen: ${result.synced}/${result.total} synchronisiert, ` +
          `${result.errors} Fehler${suffix}`
      )
    },
    onError: (e) => toast.error(apiError(e)),
  })

  // Grid-Spalten: header als String, damit die Spalten-Auswahl ihn anzeigen kann
  const columns = useMemo<ColumnDef<IdentitySyncJournalEntry>[]>(
    () => [
      {
        id: "createdAt",
        header: "Zeitpunkt",
        // ISO-String sortiert chronologisch korrekt
        accessorFn: (entry) => entry.createdAt,
        cell: ({ row }) => fmtTimestamp(row.original.createdAt),
        meta: { cellClassName: "tabular-nums" },
      },
      {
        id: "employeeId",
        header: "Mitarbeiter-ID",
        accessorFn: (entry) => entry.employeeId,
        meta: { cellClassName: "tabular-nums" },
      },
      {
        id: "eventType",
        header: "Ereignis",
        accessorFn: (entry) => entry.eventType,
        cell: ({ row }) => (
          <Badge variant="outline" className={EVENT_BADGE[row.original.eventType] ?? ""}>
            {row.original.eventType}
          </Badge>
        ),
      },
      {
        id: "summary",
        header: "Zusammenfassung",
        accessorFn: (entry) => entry.summary,
        cell: ({ row }) => (
          <span className="text-muted-foreground" title={row.original.details ?? undefined}>
            {row.original.summary}
          </span>
        ),
      },
    ],
    [],
  )

  if (status.isLoading) return <LoadingSpinner fullPage />

  const data = status.data

  return (
    <div className="space-y-3">
      {data?.dryRun && (
        <div className="flex items-center gap-2 rounded-md border border-warning bg-warning/10 px-3 py-2 text-sm text-amber-800">
          <AlertTriangle className="size-4 shrink-0" />
          Dry-Run-Modus: Keine Logto-M2M-Zugangsdaten konfiguriert — der Sync
          protokolliert nur, schreibt aber nichts nach Logto.
        </div>
      )}

      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="text-sm text-muted-foreground">
          Fehler im Journal:{" "}
          <span className={data && data.errorCount > 0 ? "font-medium text-destructive" : ""}>
            {data?.errorCount ?? 0}
          </span>
        </div>
        <Button onClick={() => sync.mutate()} disabled={sync.isPending}>
          <RefreshCw className={sync.isPending ? "animate-spin" : ""} /> Voll-Sync starten
        </Button>
      </div>

      <Card>
        <CardContent className="py-2">
          <DataGrid
            columns={columns}
            data={data?.entries ?? []}
            storageKey="identity"
            emptyMessage="Noch keine Sync-Journal-Einträge."
          />
        </CardContent>
      </Card>
    </div>
  )
}
